import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentPetBreedingStopLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {NatPutPetBreedingStopRequest, NatPutPetBreedingStopResponse} from '../repository/NatPutPetBreedingStop'
import NatResidentPetBreedingStopForm from './resident/NatResidentPetBreedingStopForm'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentPetBreedingStopLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    petType: Nullable<string>
    petBreed: Nullable<string>
    havePetEndDate: Nullable<string>
    havePetEndReason: Nullable<string>
    otherReason: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentPetBreedingStopInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            petType: null,
            petBreed: null,
            havePetEndDate: null,
            havePetEndReason: null,
            otherReason: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applyId, condominiumCd, applicationRepo, eventEmitter} = this.props
        const applyInfo = this.props.location.state?.applyInfo

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultPetType = applyInfo?.petType ?? null
        let defaultPetBreed = applyInfo?.petBreed ?? null
        let defaultHavePetEndDate = applyInfo?.havePetEndDate ?? null
        let defaultHavePetEndReason = applyInfo?.havePetEndReason ?? null
        let defaultOtherReason = applyInfo?.otherReason ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const petBreedingStopDetail =
                    await this.props.asyncCallFunction(
                        applicationRepo.getPetBreedingStopForResident.bind(
                            applicationRepo, condominiumCd, applyId,
                        ),
                        (httpErrorOrNetworkError) => {
                            eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                        },
                    )
                if (petBreedingStopDetail?.details) {
                    defaultPetType = petBreedingStopDetail.details.petType
                    defaultPetBreed = petBreedingStopDetail.details.petBreed
                    defaultHavePetEndDate = petBreedingStopDetail.details.havePetEndDate
                    defaultHavePetEndReason = petBreedingStopDetail.details.havePetEndReason
                    defaultOtherReason = petBreedingStopDetail.details.otherReason
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }
        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            petType: defaultPetType,
            petBreed: defaultPetBreed,
            havePetEndDate: defaultHavePetEndDate,
            havePetEndReason: defaultHavePetEndReason,
            otherReason: defaultOtherReason,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutPetBreedingStopRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutPetBreedingStopResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyPetBreedingStop.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentPetBreedingStopLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        petType: request.petType,
                        petBreed: request.petBreed,
                        havePetEndDate: request.havePetEndDate,
                        havePetEndReason: request.havePetEndReason,
                        otherReason: request.otherReason,
                    },
                    applyId: this.props.applyId,
                }
                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/petBreedingStop/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/petBreedingStop/${this.props.applyId}`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    ペット飼育中止届
                </PageTitle>
                <Box height={'20px'}/>
                <Box>
                    マンション内で自身が飼育している犬や猫について、飼育を中止する場合に提出が必要です。
                </Box>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentPetBreedingStopForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentPetBreedingStopInputPage)
